import React from "react";
import { Search, X, Filter, RotateCcw } from "lucide-react";
import { WORKSHOP_CATEGORIES, WORKSHOP_MODES } from "@/types/workshop";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * ============================================================
 * WORKSHOP FILTERS TOOLBAR
 * ============================================================
 * Provides instant searching and multi-dimension filtering
 * across technical categories and delivery modes.
 */
export default function WorkshopFilters({
  search = "",
  onSearchChange,
  selectedCategory = "All",
  onCategoryChange,
  selectedMode = "All",
  onModeChange,
  totalCount = 0,
  onReset,
}) {
  const categoryOptions = ["All", ...WORKSHOP_CATEGORIES];

  const modeOptions = [
    { label: "All Modes", value: "All" },
    { label: "Online", value: "Online" },
    { label: "In-Person", value: "Offline" }, // Value remains 'Offline' per architecture
    { label: "Hybrid", value: "Hybrid" },
  ];

  const isFiltered =
    Boolean(search.trim()) || selectedCategory !== "All" || selectedMode !== "All";

  return (
    <div className="space-y-6 mb-10">
      {/* Top Bar: Search Input & Mode Toggles */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Search Field */}
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="text"
            placeholder="Search by topic, keyword, or instructor..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-9 h-11 bg-white/[0.04] border-white/15 text-white placeholder:text-white/35 rounded-2xl text-xs focus-visible:ring-primary focus-visible:border-primary/50 transition-colors"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Mode Filter Buttons */}
        <div className="flex items-center gap-1.5 p-1 rounded-2xl border border-white/10 bg-white/[0.03] self-start md:self-auto overflow-x-auto">
          {modeOptions.map((opt) => {
            const isSelected = selectedMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onModeChange(opt.value)}
                className={cn(
                  "px-3.5 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 whitespace-nowrap",
                  isSelected
                    ? "bg-primary text-black font-semibold shadow-sm shadow-primary/30"
                    : "text-white/60 hover:text-white hover:bg-white/[0.06]"
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold text-white/40 uppercase tracking-wider mr-2 hidden sm:flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-primary" />
          <span>Category:</span>
        </div>

        {categoryOptions.map((cat) => {
          const isSelected = selectedCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => onCategoryChange(cat)}
              className={cn(
                "px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border",
                isSelected
                  ? "bg-primary/20 border-primary/50 text-white font-semibold shadow-sm shadow-primary/20"
                  : "bg-white/[0.03] border-white/10 text-white/65 hover:bg-white/[0.07] hover:border-white/20 hover:text-white"
              )}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Filter Status Summary & Reset Action */}
      <div className="flex items-center justify-between text-xs text-white/50 pt-2 border-t border-white/5">
        <div>
          Showing <span className="text-white font-semibold">{totalCount}</span>{" "}
          {totalCount === 1 ? "workshop" : "workshops"}
          {isFiltered && <span className="text-white/40"> (filtered)</span>}
        </div>

        {isFiltered && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    </div>
  );
}
